import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";

const EMPTY_COUPON = {
  code: "",
  type: "percentage",
  value: 10,
  min_subtotal: 0,
  max_uses: null,
  expires_at: null,
  active: true,
  description: "",
};

const AdminCoupons = () => {
  const { t } = useLang();
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_COUPON);

  const load = () => api.get("/coupons").then((r) => setCoupons(r.data || []));

  useEffect(() => {
    load();
  }, []);

  const open = (c) => {
    setEditing(c?.id || "new");
    setForm(c ? { ...EMPTY_COUPON, ...c } : EMPTY_COUPON);
  };
  const close = () => { setEditing(null); setForm(EMPTY_COUPON); };

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        code: form.code.toUpperCase().trim(),
        value: parseFloat(form.value || 0),
        min_subtotal: parseFloat(form.min_subtotal || 0),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
      };
      if (editing === "new") {
        await api.post("/coupons", payload);
        toast.success("Coupon created");
      } else {
        await api.patch(`/coupons/${editing}`, payload);
        toast.success("Updated");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await api.delete(`/coupons/${id}`);
    toast.success("Deleted");
    load();
  };

  const typeLabel = (t_) => {
    if (t_ === "percentage") return t("admin.typePercentage");
    if (t_ === "fixed") return t("admin.typeFixed");
    if (t_ === "free_shipping") return t("admin.typeFreeShipping");
    return t_;
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => open(null)} className="luxury-btn" data-testid="add-coupon-btn">
          <Plus size={16} /> {t("admin.addCoupon")}
        </button>
      </div>
      <div className="bg-white border border-[#EBE5DF] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F2ED]">
            <tr>
              <th className="text-left px-4 py-3">{t("admin.code")}</th>
              <th className="text-left px-4 py-3">{t("admin.type")}</th>
              <th className="text-left px-4 py-3">{t("admin.value")}</th>
              <th className="text-left px-4 py-3">{t("admin.minSubtotal")}</th>
              <th className="text-left px-4 py-3">{t("admin.uses")} / {t("admin.maxUses")}</th>
              <th className="text-left px-4 py-3">{t("admin.active")}</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id} className="border-t border-[#EBE5DF]" data-testid={`coupon-row-${c.id}`}>
                <td className="px-4 py-3 font-mono font-semibold">{c.code}</td>
                <td className="px-4 py-3 text-[#736B66]">{typeLabel(c.type)}</td>
                <td className="px-4 py-3">
                  {c.type === "percentage" && `${c.value}%`}
                  {c.type === "fixed" && `${c.value.toFixed(2)}€`}
                  {c.type === "free_shipping" && "—"}
                </td>
                <td className="px-4 py-3 text-[#736B66]">{c.min_subtotal > 0 ? `${c.min_subtotal.toFixed(2)}€` : "—"}</td>
                <td className="px-4 py-3 text-[#736B66]">{c.uses || 0} / {c.max_uses ?? "∞"}</td>
                <td className="px-4 py-3">{c.active ? "✓" : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => open(c)} className="text-[#2C1E16] hover:text-[#D4AF37] mr-3" data-testid={`edit-coupon-${c.id}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(c.id)} className="text-[#736B66] hover:text-[#8B0000]" data-testid={`delete-coupon-${c.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {coupons.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-[#736B66]">No coupons yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white max-w-xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="coupon-form">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">{editing === "new" ? t("admin.addCoupon") : t("admin.editCoupon")}</h2>
              <button onClick={close}><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="overline mb-1 block">{t("admin.code")}</label>
                <input className="luxury-input-box font-mono uppercase" value={form.code} onChange={onChange("code")} data-testid="coupon-code" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.type")}</label>
                <select className="luxury-input-box" value={form.type} onChange={onChange("type")} data-testid="coupon-type">
                  <option value="percentage">{t("admin.typePercentage")}</option>
                  <option value="fixed">{t("admin.typeFixed")}</option>
                  <option value="free_shipping">{t("admin.typeFreeShipping")}</option>
                </select>
              </div>
              {form.type !== "free_shipping" && (
                <div>
                  <label className="overline mb-1 block">{t("admin.value")} {form.type === "percentage" ? "(%)" : "(€)"}</label>
                  <input type="number" step="0.01" className="luxury-input-box" value={form.value} onChange={onChange("value")} data-testid="coupon-value" />
                </div>
              )}
              <div>
                <label className="overline mb-1 block">{t("admin.minSubtotal")} (€)</label>
                <input type="number" step="0.01" className="luxury-input-box" value={form.min_subtotal} onChange={onChange("min_subtotal")} />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.maxUses")}</label>
                <input type="number" className="luxury-input-box" value={form.max_uses || ""} onChange={onChange("max_uses")} placeholder="∞" />
              </div>
              <div>
                <label className="overline mb-1 block">{t("admin.expiresAt")}</label>
                <input type="datetime-local" className="luxury-input-box" value={form.expires_at ? form.expires_at.slice(0, 16) : ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : null })} />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-1 block">{t("admin.description")}</label>
                <input className="luxury-input-box" value={form.description || ""} onChange={onChange("description")} />
              </div>
              <div className="sm:col-span-2 flex items-center gap-2">
                <input type="checkbox" id="coupon-active" checked={!!form.active} onChange={onChange("active")} />
                <label htmlFor="coupon-active" className="text-sm">{t("admin.active")}</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={close} className="luxury-btn-outline">{t("admin.cancel")}</button>
              <button onClick={save} className="luxury-btn" data-testid="save-coupon">{t("admin.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
